import React from "react";
import { LeadCompareIdentity } from "../types";

// =============================================================================
// LEAD COMPARE HEADER — Step 6 (lead-phone-normalization plan), Layout A hero.
// =============================================================================
// Two leads stacked in two aligned columns; each id field (email/phone/instagram)
// carries a SAME / DIFFERS marker so "is this the same person?" reads in ~2s.
// READ-ONLY — no merge, no write. Mako 2026-09-11: "layout A is the best we have
// to do that." The marker is a PAIRWISE result (left vs right), so it is emitted
// ONCE per field, on the right/candidate column — E5's getByTestId assertion is
// strict-mode single-locator, so double-emission is a bug not a nicety.
// =============================================================================

interface LeadCompareHeaderProps {
  left: LeadCompareIdentity | null;
  right: LeadCompareIdentity | null;
  onCloseCompare: () => void;
}

type Field = "email" | "phone" | "instagram";
type Mark = "same" | "differs" | null;

const norm: Record<Field, (v: string | null) => string> = {
  email: (v) => (v || "").trim().toLowerCase(),
  phone: (v) => (v || "").replace(/[^0-9]/g, ""),
  instagram: (v) => (v || "").replace(/^@+/, "").trim().toLowerCase(),
};

function markOf(
  field: Field,
  left: LeadCompareIdentity | null,
  right: LeadCompareIdentity | null
): Mark {
  const l = left ? left[field] : null;
  const r = right ? right[field] : null;
  if (!l || !r) return null; // either side missing → no chip
  return norm[field](l) === norm[field](r) ? "same" : "differs";
}

const FIELD_GLYPH: Record<Field, string> = { email: "✉", phone: "☎", instagram: "@" };

const MarkChip: React.FC<{ field: Field; mark: Mark }> = ({ field, mark }) => {
  if (!mark) return null;
  const cls =
    mark === "same"
      ? "text-ds-green bg-ds-green/10 border-ds-green/30"
      : "text-ds-amber bg-ds-amber-bg border-ds-amber/30";
  return (
    <span
      data-testid={`compare-mark-${field}`}
      data-mark={mark}
      className={`ml-auto flex-shrink-0 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${cls}`}
    >
      {mark}
    </span>
  );
};

const IdRow: React.FC<{ field: Field; value: string | null; mark: Mark }> = ({
  field,
  value,
  mark,
}) => (
  <div className="flex items-center gap-2 py-0.5">
    <span className="text-glass-muted w-4 text-center flex-shrink-0" aria-hidden>
      {FIELD_GLYPH[field]}
    </span>
    <span className="font-mono text-sm text-glass truncate" title={value || undefined}>
      {value || <span className="text-glass-muted italic">—</span>}
    </span>
    <MarkChip field={field} mark={mark} />
  </div>
);

const Column: React.FC<{
  identity: LeadCompareIdentity | null;
  marks: Record<Field, Mark>;
  showMarks: boolean;
}> = ({ identity, marks, showMarks }) => (
  <div className="min-w-0 px-4 py-2">
    <div className="font-semibold text-glass truncate mb-1">
      {identity?.name || identity?.email?.split("@")[0] || (
        <span className="text-glass-muted">—</span>
      )}
    </div>
    <IdRow field="email" value={identity?.email ?? null} mark={showMarks ? marks.email : null} />
    <IdRow field="phone" value={identity?.phone ?? null} mark={showMarks ? marks.phone : null} />
    <IdRow
      field="instagram"
      value={identity?.instagram ?? null}
      mark={showMarks ? marks.instagram : null}
    />
  </div>
);

const LeadCompareHeader: React.FC<LeadCompareHeaderProps> = ({ left, right, onCloseCompare }) => {
  const marks: Record<Field, Mark> = {
    email: markOf("email", left, right),
    phone: markOf("phone", left, right),
    instagram: markOf("instagram", left, right),
  };
  const sharedPhone = right?.phone ?? left?.phone ?? null;

  return (
    <div data-testid="lead-compare-header" className="border-b border-ds-accent/30">
      {/* Top bar — reuses the suggested-match banner strip look. */}
      <div className="flex items-center gap-2 flex-wrap px-6 py-1.5 border-b border-ds-accent/30 text-sm bg-ds-accent/10">
        <span className="text-[10px] font-bold uppercase tracking-wider text-glass-muted">
          Comparing
        </span>
        <span className="text-glass-muted">·</span>
        {sharedPhone && (
          <span className="text-glass-muted">
            shares this phone number: <span className="font-mono text-glass">{sharedPhone}</span>
          </span>
        )}
        <button
          type="button"
          data-testid="lead-compare-close"
          onClick={onCloseCompare}
          aria-label="Close compare"
          className="ml-auto flex-shrink-0 text-glass-muted hover:text-glass-secondary anim-tap text-xs font-semibold"
        >
          ✕ Close compare
        </button>
      </div>

      {/* Two aligned columns. Left = anchor lead (no chips); right = candidate
          (carries the same/differs chip per field). */}
      <div className="grid grid-cols-2 divide-x divide-black/[0.08]">
        <Column identity={left} marks={marks} showMarks={false} />
        <Column identity={right} marks={marks} showMarks />
      </div>
    </div>
  );
};

export default LeadCompareHeader;
